import {
  EmailDigestPrefResponseSchema,
  NotificationPrefsResponseSchema,
  QuietHoursResponseSchema,
  type CoarsePreset,
  type EmailDigestPrefResponse,
  type NotificationPrefsResponse,
  type QuietHours,
  type QuietHoursResponse,
  type UpdateNotificationPrefInput,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Phase 20 v1.4 — NOTIF-01. Per-kind notification preference endpoints.
 *
 * - `getNotificationPrefs` returns the full map (empty object = all kinds
 *   opted-in by default — dispatcher reads `prefs[kind]?.channel ?? true`).
 * - `updateNotificationPref` PUTs a single kind's toggle(s); server merges
 *   into the existing map and returns the updated full map for client cache.
 *
 * Backend contract lives in apps/api/src/modules/users/users.controller.ts.
 */
export class UsersEndpoints {
  constructor(private readonly client: ApiClient) {}

  getNotificationPrefs(): Promise<NotificationPrefsResponse> {
    return this.client.get('/me/notification-prefs', {
      responseSchema: NotificationPrefsResponseSchema,
    });
  }

  updateNotificationPref(
    input: UpdateNotificationPrefInput,
  ): Promise<NotificationPrefsResponse> {
    return this.client.patch(
      '/me/notification-prefs',
      input,
      { responseSchema: NotificationPrefsResponseSchema },
    );
  }

  /**
   * Phase 40 v1.9 — NOTIF-COARSE-01. PUT /me/notification-prefs/coarse —
   * apply one of the 3 coarse presets (`all` | `essential` | `none`) across
   * every non-transactional kind atomically. Returns the updated full prefs
   * map so the settings page can rebuild its grouped matrix in one round-
   * trip.
   */
  applyCoarsePreset(
    preset: CoarsePreset,
  ): Promise<NotificationPrefsResponse> {
    return this.client.put(
      '/me/notification-prefs/coarse',
      { preset },
      { responseSchema: NotificationPrefsResponseSchema },
    );
  }

  /**
   * Phase 44 v1.10 — DIGEST-01. GET /me/notification-prefs/email-digest —
   * read the current weekly digest opt-in flag. The mobile settings page
   * calls this on load to render the switch row initial state.
   */
  getEmailDigestOptIn(): Promise<EmailDigestPrefResponse> {
    return this.client.get('/me/notification-prefs/email-digest', {
      responseSchema: EmailDigestPrefResponseSchema,
    });
  }

  /**
   * Phase 44 v1.10 — DIGEST-01. PUT /me/notification-prefs/email-digest —
   * toggle the weekly email digest opt-in. Default OFF per LGPD; idempotent.
   * Returns the new value so the client can reconcile its local state without
   * an extra GET.
   */
  setEmailDigestOptIn(optIn: boolean): Promise<EmailDigestPrefResponse> {
    return this.client.put(
      '/me/notification-prefs/email-digest',
      { optIn },
      { responseSchema: EmailDigestPrefResponseSchema },
    );
  }

  /**
   * Phase 45 v1.10 — SNOOZE-01. GET /me/notification-prefs/quiet-hours —
   * read the user's configured quiet hours window. Returns { quietHours: null }
   * when no window is set.
   */
  getQuietHours(): Promise<QuietHoursResponse> {
    return this.client.get('/me/notification-prefs/quiet-hours', {
      responseSchema: QuietHoursResponseSchema,
    });
  }

  /**
   * Phase 45 v1.10 — SNOOZE-01. PUT /me/notification-prefs/quiet-hours —
   * set or clear the quiet hours window. Pass `null` to disable. Transactional
   * notification kinds bypass quiet hours at dispatcher level.
   */
  setQuietHours(window: QuietHours | null): Promise<QuietHoursResponse> {
    const body = window
      ? { mode: 'set' as const, start: window.start, end: window.end }
      : { mode: 'clear' as const };
    return this.client.put('/me/notification-prefs/quiet-hours', body, {
      responseSchema: QuietHoursResponseSchema,
    });
  }
}
